import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { AdminEditModeProvider } from "@/contexts/AdminEditModeContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useEmailNotifications } from "@/hooks/useEmailNotifications";
import ScrollToTop from "@/components/ScrollToTop";
import { useAdminTheme } from "@/hooks/useAdminTheme";
import { lazy, Suspense, useState } from "react";
import { Loader2 } from "lucide-react";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";

const AdminEditToolbar = lazy(() => import("@/components/admin/AdminEditToolbar").then(m => ({ default: m.AdminEditToolbar })));

// Lazy load Index page for better initial bundle size
const Index = lazy(() => import("./pages/Index"));

// Lazy load non-critical UI components
const KeyboardShortcutsDialog = lazy(() => import("@/components/KeyboardShortcutsDialog").then(m => ({ default: m.KeyboardShortcutsDialog })));
const VideoCallNotification = lazy(() => import("@/components/VideoCallNotification").then(m => ({ default: m.VideoCallNotification })));

// Lazy load pages for better performance
const Auth = lazy(() => import("./pages/Auth"));
const SignIn = lazy(() => import("./pages/SignIn"));
const SignUp = lazy(() => import("./pages/SignUp"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Survey = lazy(() => import("./pages/SmartMatching"));
const Matches = lazy(() => import("./pages/Matches"));
const Chat = lazy(() => import("./pages/Chat"));
const Messages = lazy(() => import("./pages/Messages"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Collaborations = lazy(() => import("./pages/Collaborations"));
const CollaborationDetails = lazy(() => import("./pages/CollaborationDetails"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetails = lazy(() => import("./pages/ProjectDetails"));
const EditorPortfolio = lazy(() => import("./pages/EditorPortfolio"));
const BecomeEditor = lazy(() => import("./pages/BecomeEditor"));
const EditorOnboarding = lazy(() => import("./pages/EditorOnboarding"));
const EditorDashboard = lazy(() => import("./pages/EditorDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const FindEditor = lazy(() => import("./pages/FindEditor"));
const FileBrowser = lazy(() => import("./pages/FileBrowser"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Invoices = lazy(() => import("./pages/Invoices"));
const ContractView = lazy(() => import("./pages/ContractView"));
const Achievements = lazy(() => import("./pages/Achievements"));
const Compare = lazy(() => import("./pages/Compare"));
const Hub = lazy(() => import("./pages/Hub"));
const PostDetail = lazy(() => import("./pages/PostDetail"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const EditorTutorial = lazy(() => import("./pages/EditorTutorial"));
const ModerationPanel = lazy(() => import("./pages/ModerationPanel"));
const PlatformAnalytics = lazy(() => import("./pages/PlatformAnalytics"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Help = lazy(() => import("./pages/Help"));
const NotFound = lazy(() => import("./pages/NotFound"));
const EmailSettings = lazy(() => import("./pages/EmailSettings"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const AdminEmailLogs = lazy(() => import("./pages/AdminEmailLogs"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const MarketplaceTemplate = lazy(() => import("./pages/MarketplaceTemplate"));
const MonitoringDashboard = lazy(() => import("./pages/MonitoringDashboard"));
const ContractTemplates = lazy(() => import("./pages/ContractTemplates"));
const Profile = lazy(() => import("./pages/Profile"));
const GoogleOAuthCallback = lazy(() => import("./pages/GoogleOAuthCallback"));
const ChooseUsername = lazy(() => import("./pages/ChooseUsername"));
const ChooseRole = lazy(() => import("./pages/ChooseRole"));
const LogoVariants = lazy(() => import("./pages/LogoVariants"));
const Partnerships = lazy(() => import("./pages/Partnerships"));
const PartnershipDetails = lazy(() => import("./pages/PartnershipDetails"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile"));

// SEO Service Landing Pages
const YouTubeVideoEditing = lazy(() => import("./pages/services/YouTubeVideoEditing"));
const SocialMediaVideoEditing = lazy(() => import("./pages/services/SocialMediaVideoEditing"));
const CorporateVideoEditing = lazy(() => import("./pages/services/CorporateVideoEditing"));
const WeddingVideoEditing = lazy(() => import("./pages/services/WeddingVideoEditing"));

// Marketing Landing Pages
const JoinAsEditor = lazy(() => import("./pages/JoinAsEditor"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient();

const AppContent = () => {
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useKeyboardShortcuts(
    () => setSearchOpen(true),
    () => setShortcutsOpen(true)
  );

  // Enable email notifications
  useEmailNotifications();

  // Apply admin-configured theme
  useAdminTheme();

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/logos" element={<LogoVariants />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/google-callback" element={<GoogleOAuthCallback />} />
          <Route path="/choose-username" element={<ChooseUsername />} />
          <Route path="/choose-role" element={<ChooseRole />} />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/survey" element={<Survey />} />
          <Route path="/matches" element={<Matches />} />
          <Route path="/chat/:editorId" element={
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          } />
          <Route path="/messages" element={
            <ProtectedRoute>
              <Messages />
            </ProtectedRoute>
          } />
          <Route path="/collaborations" element={
            <ProtectedRoute>
              <Collaborations />
            </ProtectedRoute>
          } />
          <Route path="/collaboration/:collaborationId" element={
            <ProtectedRoute>
              <CollaborationDetails />
            </ProtectedRoute>
          } />
          {/* Legacy project routes → redirect to messages/chat */}
          <Route path="/projects" element={
            <ProtectedRoute>
              <Projects />
            </ProtectedRoute>
          } />
          <Route path="/project/:projectId" element={
            <ProtectedRoute>
              <ProjectDetails />
            </ProtectedRoute>
          } />
          <Route path="/editor/:id" element={<EditorPortfolio />} />
          <Route path="/become-editor" element={<BecomeEditor />} />
          <Route path="/editor-onboarding" element={
            <ProtectedRoute>
              <EditorOnboarding />
            </ProtectedRoute>
          } />
          <Route path="/editor-dashboard" element={
            <ProtectedRoute>
              <EditorDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/emails" element={
            <ProtectedRoute>
              <AdminEmailLogs />
            </ProtectedRoute>
          } />
          <Route path="/admin/monitoring" element={
            <ProtectedRoute>
              <MonitoringDashboard />
            </ProtectedRoute>
          } />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/find-editor" element={<FindEditor />} />
          <Route path="/file-browser" element={
            <ProtectedRoute>
              <FileBrowser />
            </ProtectedRoute>
          } />
          <Route path="/files" element={
            <ProtectedRoute>
              <FileBrowser />
            </ProtectedRoute>
          } />
          <Route path="/portfolio" element={
            <ProtectedRoute>
              <Portfolio />
            </ProtectedRoute>
          } />
          <Route path="/invoices" element={
            <ProtectedRoute>
              <Invoices />
            </ProtectedRoute>
          } />
          <Route path="/contract/:contractId" element={
            <ProtectedRoute>
              <ContractView />
            </ProtectedRoute>
          } />
          <Route path="/achievements" element={
            <ProtectedRoute>
              <Achievements />
            </ProtectedRoute>
          } />
          <Route path="/compare" element={<Compare />} />
          <Route path="/hub" element={<Hub />} />
          <Route path="/hub/post/:postId" element={<PostDetail />} />
          <Route path="/notifications" element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          } />
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          } />
          <Route path="/editor-tutorial" element={
            <ProtectedRoute>
              <EditorTutorial />
            </ProtectedRoute>
          } />
          <Route path="/moderation" element={
            <ProtectedRoute>
              <ModerationPanel />
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute>
              <PlatformAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/help" element={<Help />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/settings/email" element={
            <ProtectedRoute>
              <EmailSettings />
            </ProtectedRoute>
          } />
          <Route path="/settings/account" element={
            <ProtectedRoute>
              <AccountSettings />
            </ProtectedRoute>
          } />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/marketplace/:templateId" element={<MarketplaceTemplate />} />
          <Route path="/templates" element={
            <ProtectedRoute>
              <ContractTemplates />
            </ProtectedRoute>
          } />
          <Route path="/partnerships" element={
            <ProtectedRoute>
              <Partnerships />
            </ProtectedRoute>
          } />
          <Route path="/partnerships/:partnershipId" element={
            <ProtectedRoute>
              <PartnershipDetails />
            </ProtectedRoute>
          } />
          <Route path="/partnerships/:partnershipId/new-project" element={
            <ProtectedRoute>
              <PartnershipDetails />
            </ProtectedRoute>
          } />
          <Route path="/profile/:userId" element={<Profile />} />
          
          {/* SEO Service Landing Pages */}
          <Route path="/services/youtube-video-editing" element={<YouTubeVideoEditing />} />
          <Route path="/services/social-media-video-editing" element={<SocialMediaVideoEditing />} />
          <Route path="/services/corporate-video-editing" element={<CorporateVideoEditing />} />
          <Route path="/services/wedding-video-editing" element={<WeddingVideoEditing />} />
          
          {/* Marketing Landing Pages */}
          <Route path="/join-as-editor" element={<JoinAsEditor />} />
          
          {/* Profile Completion (from email campaigns) */}
          <Route path="/complete-profile" element={
            <ProtectedRoute>
              <CompleteProfile />
            </ProtectedRoute>
          } />
          
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <Sonner />
      <Suspense fallback={null}>
        <VideoCallNotification />
      </Suspense>
      <Suspense fallback={null}>
        <AdminEditToolbar />
      </Suspense>
      <TooltipProvider>
        <Suspense fallback={null}>
          <KeyboardShortcutsDialog 
            open={shortcutsOpen}
            onOpenChange={setShortcutsOpen}
          />
        </Suspense>
      </TooltipProvider>
    </>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <AuthProvider>
                <AdminEditModeProvider>
                  <AppContent />
                </AdminEditModeProvider>
              </AuthProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
